import { useState, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox, Radio } from 'antd'
import { EmployeeTagType, EmployeeTagDto, Country, TalentsSeasonStatus, EmployeeEmploymentStatusDto } from '../../types'
import PerText from '../../components/custom_components/Text'
import { PerPopoverFooter } from '../../components/custom_components/PopoverFooter/PopoverFooter'
import PerFlexRow from '../../components/custom_components/FlexRow'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import { TFunction } from 'i18next'
import { RadioChangeEvent } from 'antd/lib/radio'
import useHandleEmployeeTags from '../../graphql/ranking/hooks/useHandleEmployeeTag'
import shallow from 'zustand/shallow'
import { useCultivatingTalentsStore } from './CultivatingTalentsStore'
import { PerModal } from '../../components/custom_components/Modal'
import { showNotification } from '../../components/custom_components/Notification/notification'

type RankingSetTagPopoverProps = {
  employeeFullName: string
  employeeFirstName: string
  employeeLastName: string
  isRankingEnabled: boolean
  tags: EmployeeTagDto[]
  settingsTopPercentage: number
  employeeId: string
  companyId: string
  country: Country
  isVisible: boolean
  setIsVisible: (value: boolean) => void
}

const EmployeeTags = { ...EmployeeTagType, ...{ untagged: 'untagged' } }

const filterMainTag = (tags: EmployeeTagDto[]) => {
  const mainTag = tags
    .filter((tag) => tag.type === EmployeeTags.Top || tag.type === EmployeeTags.Low || tag.type === EmployeeTags.EntryLevelLow)
    .map((tag) => (tag.type === EmployeeTags.EntryLevelLow ? EmployeeTags.Low : tag.type))
  return !!mainTag.length ? mainTag[0] : EmployeeTags.untagged
}

const filterExpertiseTags = (tags: EmployeeTagDto[]) => {
  return tags
    .filter((tag) => tag.type !== EmployeeTags.Top && tag.type !== EmployeeTags.Low && tag.type !== EmployeeTags.EntryLevelLow)
    .map((tag) => tag.type)
}

const expertiseTagsOptions = (t: TFunction) => {
  return [EmployeeTags.Sales, EmployeeTags.Speaker, EmployeeTags.Coach, EmployeeTags.Expert].map((elem) => {
    return { label: t(`global.employeeTags.performance.${elem}`), value: `${elem}` }
  })
}

const PopoverConfirmationTooltip = () => {
  const { t } = useTranslation()
  return (
    <>
      <PerText>{t(`tooltip.confirm`)}</PerText>
    </>
  )
}

const PopoverErrorTooltip = () => {
  const { t } = useTranslation()
  return (
    <>
      <PerText>{t(`tooltip.error`)}</PerText>
    </>
  )
}

// TODO: Component logic has been changed to comply to v2 breaking v1
export const RankingPerformancePopover: FC<RankingSetTagPopoverProps> = ({
  employeeFullName,
  employeeFirstName,
  employeeLastName,
  isRankingEnabled,
  tags,
  settingsTopPercentage, // TODO: this is useless since inside this component we are getting the full settings from the store
  employeeId,
  companyId,
  country,
  isVisible,
  setIsVisible,
}) => {
  const { t } = useTranslation()

  const [isProcessing, toggleProcessing] = useState<boolean>(false)
  const [mainTag, setMainTag] = useState<string>(filterMainTag(tags))
  const [expertiseTags, setExpertiseTags] = useState<CheckboxValueType[]>(filterExpertiseTags(tags))
  const [isConfirmLowTagAssignmentModalOpen, setIsConfirmLowTagAssignmentModalOpen] = useState(false)
  const [ongoingAppraisal, toggleOngoingAppraisal] = useState(false)
  const [futureEmploymentChanges, toggleFutureEmploymentChanges] = useState<EmployeeEmploymentStatusDto | undefined>(undefined)
  const [hasTooltipError, toggleTooltipError] = useState(false)

  const handleConfirmLowTagAssignmentModalCancel = () => {
    setMainTag(filterMainTag(tags))
    setIsConfirmLowTagAssignmentModalOpen(false)
  }

  // TODO: employees will be empty for v1
  const [employees, employeesWithoutRank, talentsSeasonSettings] = useCultivatingTalentsStore(
    (state) => [state.employees, state.employeesWithoutRank, state.talentsSeasonSettings],
    shallow,
  )
  const referenceYear = talentsSeasonSettings ? talentsSeasonSettings.year : new Date().getUTCFullYear()
  const rankingInProgress = talentsSeasonSettings
    ? [TalentsSeasonStatus.Opened, TalentsSeasonStatus.Confirmed].includes(talentsSeasonSettings.talentsSeasonStatus)
    : false // TODO: This is not true for v1
  const topPercentage: number = talentsSeasonSettings ? talentsSeasonSettings.topPercentage : settingsTopPercentage // TODO: Right now they should be the same value and settings cannot be undefined, since this is only v2 compliant

  const { addEmployeeTag, removeEmployeeTag, employeeHasOngoingAppraisal, employeeHasFutureEmploymentChange } = useHandleEmployeeTags(
    companyId,
    country,
    referenceYear,
    rankingInProgress,
  )

  const tagList = isRankingEnabled
    ? employees.map(({ tags }) => {
        return tags
      })
    : employeesWithoutRank.map(({ tags }) => {
        return tags
      })
  const canAssignTopTag =
    tagList.filter((tags) => tags.some((tag) => tag.type === EmployeeTags.Top)).length < Math.round(tagList.length * (topPercentage / 100))

  const hasPermanentLowTag = tags.filter((tag) => tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow).length > 0

  const disableMainPerformanceTags = hasPermanentLowTag

  const handleChangeMainTag = (e: RadioChangeEvent) => {
    const { value } = e.target
    setMainTag(value || EmployeeTags.untagged)
  }

  const handleChangeExpertiseTags = (values: CheckboxValueType[]) => {
    setExpertiseTags(values)
  }

  const handleConfirmLowTagAssignmentModalConfirm = async () => {
    toggleProcessing(true)

    const addedTags = [...expertiseTags, mainTag]
      .filter((tag) => tag !== EmployeeTags.untagged)
      .filter((tag) => !tags.some((t) => t.type === tag))

    const removedTags = tags.filter((tag) => ![...expertiseTags, mainTag].includes(tag.type))

    if (!!removedTags.length) {
      await Promise.all(removedTags.map((tag) => removeEmployeeTag({ employeeId, type: tag.type as EmployeeTagType })))
    }

    if (!!addedTags.length) {
      await Promise.all(addedTags.map((tag) => addEmployeeTag(employeeId, tag as EmployeeTagType)))
    }

    if (!!removedTags.length || !!addedTags.length) {
      showNotification({
        type: 'success',
        message: t('rankingView.table.actions.tagEmployeePerformance.notifications.update.title'),
        description: t('rankingView.table.actions.tagEmployeePerformance.notifications.update.description'),
      })
    }

    setIsVisible(!isVisible)
  }

  const handleCancel = () => {
    setIsVisible(!isVisible)
  }

  const setLowTagModalContent = async () => {
    let hasOngoingAppraisal = false
    let hasFutureChanges: EmployeeEmploymentStatusDto | undefined = undefined
    if (talentsSeasonSettings && talentsSeasonSettings.blockedChangesForLow) {
      try {
        const res = await employeeHasFutureEmploymentChange(employeeId)
        hasFutureChanges = res.data.employmentStatusOfEmployee
      } catch (err) {
        toggleProcessing(false)
        toggleTooltipError(true)
        return
      }
      if (
        !hasFutureChanges.openChanges &&
        !hasFutureChanges.futureChangesForeseenByContract &&
        !hasFutureChanges.currentBonusAfterFirstJanuary
      ) {
        try {
          const res = await employeeHasOngoingAppraisal(employeeId)
          hasOngoingAppraisal = res.data.employeeHasOngoingAppraisal
        } catch (err) {
          toggleProcessing(false)
          toggleTooltipError(true)
          return
        }
      }
    }
    toggleFutureEmploymentChanges(hasFutureChanges)
    toggleOngoingAppraisal(hasOngoingAppraisal)
    toggleProcessing(false)
    setIsConfirmLowTagAssignmentModalOpen(true)
  }

  const renderTooltip = (): JSX.Element => {
    return hasTooltipError ? <PopoverErrorTooltip /> : <PopoverConfirmationTooltip />
  }

  return (
    <>
      {isConfirmLowTagAssignmentModalOpen && (
        <ConfirmLowTagAssignmentModal
          isModalOpen={isConfirmLowTagAssignmentModalOpen}
          handleConfirm={handleConfirmLowTagAssignmentModalConfirm}
          closeModal={handleConfirmLowTagAssignmentModalCancel}
          employeeFullName={employeeFullName}
          employeeFirstName={employeeFirstName}
          employeeLastName={employeeLastName}
          ongoingAppraisal={ongoingAppraisal}
          futureEmploymentChanges={futureEmploymentChanges}
          talentsSeasonSettings={talentsSeasonSettings}
        />
      )}

      <PerText appearance="label" fontSize="xs" color="primary" display="block" mb="xxs">
        {t('rankingView.table.actions.tagEmployeePerformance.groups.mainTag')}
      </PerText>

      <Radio.Group onChange={handleChangeMainTag} value={mainTag} disabled={disableMainPerformanceTags}>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.untagged}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.performance.${EmployeeTags.untagged}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow mb="xxxs">
          <Radio value={EmployeeTags.Top} disabled={!canAssignTopTag && filterMainTag(tags) !== EmployeeTags.Top}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.performance.${EmployeeTags.Top}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
        <PerFlexRow>
          <Radio value={EmployeeTags.Low}>
            <PerText appearance="label" color="secondary" fontWeight="normal">
              {t(`global.employeeTags.performance.${EmployeeTags.Low}`)}
            </PerText>
          </Radio>
        </PerFlexRow>
      </Radio.Group>

      {!canAssignTopTag && !hasPermanentLowTag && (
        <PerText appearance="label" fontWeight="thin" color="secondary" fontSize="xs" mb="xxxxs" mt="xs">
          {t('rankingView.table.actions.tagEmployeePerformance.groups.maxTopTags')}
        </PerText>
      )}

      <PerText appearance="label" fontSize="xs" color="success" display="block" mb="xxs" mt="xxs">
        {t('rankingView.table.actions.tagEmployeePerformance.groups.expertise')}
      </PerText>

      <Checkbox.Group
        options={expertiseTagsOptions(t)}
        value={expertiseTags}
        onChange={handleChangeExpertiseTags}
        className="vertical performance"
      />

      <PerPopoverFooter
        onClickCancel={handleCancel}
        onClickConfirm={
          mainTag === EmployeeTagType.Low && !hasPermanentLowTag
            ? async () => {
                toggleProcessing(true)
                setLowTagModalContent()
              }
            : handleConfirmLowTagAssignmentModalConfirm
        }
        isProcessing={isProcessing}
        cancelButtonDisabled={isProcessing}
        okButtonDisabled={hasTooltipError}
        tooltip={renderTooltip}
      />
    </>
  )
}

const ConfirmLowTagAssignmentModal = ({
  isModalOpen,
  closeModal,
  handleConfirm,
  employeeFullName,
  employeeFirstName,
  employeeLastName,
  ongoingAppraisal,
  futureEmploymentChanges,
  talentsSeasonSettings,
}) => {
  const [t] = useTranslation()

  return (
    <PerModal
      zIndex={2000}
      centered
      visible={isModalOpen}
      onCancel={closeModal}
      title={{
        heading: t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.heading'),
      }}
      footer={{
        confirmBtn: {
          onClick: handleConfirm,
          disabled:
            talentsSeasonSettings && talentsSeasonSettings.blockedChangesForLow && futureEmploymentChanges.futureChangesForeseenByContract,
        },
        cancelBtn: { onClick: closeModal },
      }}
    >
      {talentsSeasonSettings && talentsSeasonSettings.blockedChangesForLow ? (
        <>
          <PerText>
            {futureEmploymentChanges.futureChangesForeseenByContract
              ? t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.existingFutureChangesForeseenByContract', {
                  employeeFirstName,
                })
              : futureEmploymentChanges.currentBonusAfterFirstJanuary
              ? t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.bonusAfterFirstJanuary', {
                  employeeFullName,
                  employeeFirstName,
                })
              : futureEmploymentChanges.openChanges
              ? t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.existingOpenEmploymentChanges', {
                  employeeFirstName,
                })
              : ongoingAppraisal
              ? t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.description1OngoingAppraisal', {
                  employeeFirstName,
                  employeeLastName,
                })
              : t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.description1', {
                  employeeFullName,
                })}
          </PerText>
          {!futureEmploymentChanges.futureChangesForeseenByContract && (
            <>
              <PerText>{t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.description2')}</PerText>
              <ul>
                <li>
                  <PerText>{t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.item1')}</PerText>
                </li>
                <li>
                  <PerText>{t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.item2')}</PerText>
                </li>
              </ul>
            </>
          )}
        </>
      ) : (
        <>
          <PerText>
            {t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.description1', {
              employeeFullName,
            })}
          </PerText>
          <PerText>{t('rankingView.table.actions.tagEmployeePerformance.confirmLowTagAssignmentModal.description3')}</PerText>
        </>
      )}
    </PerModal>
  )
}
